import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  Loader2,
  Check,
  X,
  ChevronLeft,
  RotateCcw,
  Zap,
  Thermometer,
  Usb,
  Battery,
  BatteryCharging,
  Activity,
  HardDrive,
  Lightbulb,
  CircleDot,
  CheckCircle2,
  XCircle,
  ArrowRight,
  Hand,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import betaAPI from "v2/api";
import { showToast } from "utils/toast";
import { SelectField, TextField } from "./Manufacturing/FormField";
import {
  buildSerial,
  UNIT_TYPE_OPTIONS,
  formatApiError,
  formatMac,
} from "./Manufacturing/constants";
import {
  parseFactoryLines,
  reduceFactoryState,
  initFactoryState,
  answerBytes,
  FACTORY_TESTS,
} from "utils/factoryTest";

// A relevant icon per test slug — keeps the catalog data-only.
const TEST_ICON = {
  vcore: Zap,
  mcu_temp: Thermometer,
  usb: Usb,
  battery: Battery,
  charger: BatteryCharging,
  imu_lg: Activity,
  imu_hg: Activity,
  nor_id: HardDrive,
  nor_rw: HardDrive,
  button: CircleDot,
  leds: Lightbulb,
};

// ── Checklist row ───────────────────────────────────────────────────

const StatusIcon = ({ status }) => {
  if (status === "pass")
    return <Check size={14} className="text-[#16A34A]" strokeWidth={3} />;
  if (status === "fail")
    return <X size={14} className="text-[#DC2626]" strokeWidth={3} />;
  if (status === "running")
    return <Loader2 size={14} className="animate-spin text-[#E07A3A]" />;
  return <span className="h-1.5 w-1.5 rounded-full bg-[#D1D5DB]" />;
};

const TestRow = ({ test }) => {
  const { t } = useTranslation();
  const Icon = TEST_ICON[test.slug] || CircleDot;
  const measured =
    test.value != null
      ? `${test.value}${test.unit ? ` ${test.unit}` : ""}`
      : null;
  const tone =
    test.status === "pass"
      ? "text-[#111827]"
      : test.status === "fail"
      ? "text-[#DC2626]"
      : test.status === "running"
      ? "text-[#111827]"
      : "text-[#9CA3AF]";
  return (
    <div className="flex items-center gap-3 px-3 py-2">
      <Icon
        size={15}
        className={
          test.status === "pending" ? "text-[#D1D5DB]" : "text-[#6B7280]"
        }
      />
      <span className={`flex-1 text-[13px] font-medium ${tone}`}>
        {test.label}
        {test.interactive && (
          <span className="ml-1.5 text-[10px] font-normal text-[#9CA3AF]">
            {t("deviceTools.factoryTest.checkTag")}
          </span>
        )}
      </span>
      {measured && (
        <span className="font-mono text-[11px] text-[#6B7280]">{measured}</span>
      )}
      <StatusIcon status={test.status} />
    </div>
  );
};

// ── Interactive prompt cards ────────────────────────────────────────

const LedPrompt = ({ prompt, ledSteps, onAnswer }) => {
  const { t } = useTranslation();
  // Distinct colours seen so far, cycled while the operator decides.
  const colors = useMemo(() => {
    const seen = [];
    for (const s of ledSteps) {
      if (s.color && !seen.find((c) => c.name === s.color.name)) {
        seen.push(s.color);
      }
    }
    return seen;
  }, [ledSteps]);

  const [idx, setIdx] = useState(0);
  useEffect(() => {
    if (colors.length === 0) return;
    const iv = setInterval(() => setIdx((i) => (i + 1) % colors.length), 750);
    return () => clearInterval(iv);
  }, [colors.length]);

  const active = colors.length ? colors[idx % colors.length] : null;
  const hex = active?.hex || "#374151";

  return (
    <div className="rounded-xl border border-[#FEEAD9] bg-[#FFF9F4] p-5">
      <div className="flex flex-col items-center text-center">
        <div
          className="mb-4 flex h-20 w-20 items-center justify-center rounded-full transition-colors duration-300"
          style={{
            backgroundColor: hex,
            boxShadow: `0 0 28px 4px ${hex}80`,
          }}
        >
          <Lightbulb size={30} className="text-white/90" />
        </div>
        {colors.length > 0 && (
          <div className="mb-3 flex items-center gap-1.5">
            {colors.map((c, i) => (
              <span
                key={c.name}
                title={c.name}
                className="h-2 w-2 rounded-full transition-opacity"
                style={{
                  backgroundColor: c.hex,
                  opacity: i === idx % colors.length ? 1 : 0.35,
                }}
              />
            ))}
          </div>
        )}
        <p className="mb-4 max-w-sm text-sm font-medium text-[#111827]">
          {prompt.q || t("deviceTools.factoryTest.ledQuestion")}
        </p>
        <div className="flex w-full max-w-xs items-center gap-2.5">
          <button
            onClick={() => onAnswer(false)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] border border-[#FCA5A5] bg-white px-4 py-2.5 text-sm font-semibold text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
          >
            <X size={15} />
            {t("common.status.no")}
          </button>
          <button
            onClick={() => onAnswer(true)}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150"
            style={{
              background: "linear-gradient(135deg, #22C55E 0%, #16A34A 100%)",
            }}
          >
            <Check size={15} />
            {t("common.status.yes")}
          </button>
        </div>
      </div>
    </div>
  );
};

const ButtonPrompt = ({ prompt, remaining, onFail }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-xl border border-[#FEEAD9] bg-[#FFF9F4] p-5">
      <div className="flex flex-col items-center text-center">
        <span className="relative mb-4 flex h-20 w-20 items-center justify-center">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#E07A3A] opacity-30" />
          <span className="relative flex h-20 w-20 items-center justify-center rounded-full bg-[#E07A3A]">
            <Hand size={30} className="text-white" />
          </span>
        </span>
        <p className="mb-1 text-sm font-semibold text-[#111827]">
          {prompt.q || t("deviceTools.factoryTest.buttonPrompt")}
        </p>
        {remaining != null && (
          <p className="mb-4 font-mono text-xs text-[#9CA3AF]">
            {t("deviceTools.factoryTest.waitingSeconds", {
              seconds: remaining,
            })}
          </p>
        )}
        <button
          onClick={onFail}
          className="flex items-center justify-center gap-1.5 rounded-[10px] border border-[#FCA5A5] bg-white px-4 py-2 text-xs font-semibold text-[#DC2626] transition duration-150 hover:bg-[#FEF2F2]"
        >
          <X size={13} />
          {t("deviceTools.factoryTest.buttonNotResponding")}
        </button>
      </div>
    </div>
  );
};

// ── Main flow ───────────────────────────────────────────────────────

const PHASE = { TESTING: "testing", PASS: "pass", FAIL: "fail" };

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const FactoryTestFlow = ({
  send,
  sendRaw,
  kick,
  receivedData,
  connected,
  commandsLocked,
  onExit,
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState(initFactoryState);
  const [runId, setRunId] = useState(0);
  const scanPosRef = useRef(receivedData.length);
  // Mirror stream flags into refs so the async command loops read fresh values
  // without being in their dependency arrays.
  const startedRef = useRef(false);
  const promptIdRef = useRef(null);
  const endedRef = useRef(false);

  // MAC lookup: undefined = not looked up, null = new unit, object = registered.
  const [existing, setExisting] = useState(undefined);
  const lookupDoneRef = useRef(false);

  // Register form (new unit, passed).
  const [batches, setBatches] = useState([]);
  const [batchId, setBatchId] = useState("");
  const [seq, setSeq] = useState("");
  const [serial, setSerial] = useState("");
  const [serialEdited, setSerialEdited] = useState(false);
  const [unitType, setUnitType] = useState("production");
  const [saving, setSaving] = useState(false);
  const [regError, setRegError] = useState(null);

  // Diagnostic logging (existing unit, passed).
  const [diagState, setDiagState] = useState("idle"); // idle | logging | logged | error
  const [diagError, setDiagError] = useState(null);
  const diagStartedRef = useRef(false);

  const phase = state.ended
    ? state.ended.pass
      ? PHASE.PASS
      : PHASE.FAIL
    : PHASE.TESTING;

  // Keep the refs in sync with the stream state.
  useEffect(() => {
    startedRef.current = state.started;
  }, [state.started]);
  useEffect(() => {
    promptIdRef.current = state.prompt?.id ?? null;
  }, [state.prompt]);
  useEffect(() => {
    endedRef.current = !!state.ended;
  }, [state.ended]);

  // Start / restart: reset everything, snapshot the read cursor, run the test.
  // The device console only drains its serial RX on a DTR edge, so after sending
  // the command we nudge it (kick) until the NDJSON stream actually starts —
  // otherwise `selftest ui` just sits buffered on a cold console.
  useEffect(() => {
    scanPosRef.current = receivedData.length;
    startedRef.current = false;
    endedRef.current = false;
    setState(initFactoryState());
    setExisting(undefined);
    lookupDoneRef.current = false;
    setBatchId("");
    setSeq("");
    setSerial("");
    setSerialEdited(false);
    setRegError(null);
    setDiagState("idle");
    setDiagError(null);
    diagStartedRef.current = false;

    let cancelled = false;
    (async () => {
      // Yield a tick before sending. Under React StrictMode (dev) the effect
      // mounts, cleans up, then re-mounts; the first pass sets `cancelled` before
      // this resumes, so the command goes out exactly once instead of twice —
      // a double send would leave a spare `selftest ui` buffered on the device.
      await sleep(60);
      if (cancelled) return;
      await send("selftest ui").catch(() => {});
      // The console only drains its RX on a DTR edge; nudge it until the stream
      // starts, then stop. Never kick once the run has ended — a post-run kick
      // would drain any stray buffered command and spuriously restart the test.
      for (let i = 0; i < 8 && !cancelled; i++) {
        await sleep(1800);
        if (cancelled || startedRef.current || endedRef.current) break;
        await kick?.().catch?.(() => {});
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [runId]);

  // Passive scanner: fold each new line into the stream state.
  useEffect(() => {
    if (receivedData.length <= scanPosRef.current) return;
    const events = [];
    for (let i = scanPosRef.current; i < receivedData.length; i++) {
      events.push(...parseFactoryLines(receivedData[i]?.data));
    }
    scanPosRef.current = receivedData.length;
    if (events.length) {
      setState((prev) => events.reduce(reduceFactoryState, prev));
    }
  }, [receivedData]);

  // Once the MAC is known, look it up to decide new-vs-existing.
  useEffect(() => {
    if (!state.mac || lookupDoneRef.current) return;
    lookupDoneRef.current = true;
    let active = true;
    (async () => {
      try {
        const res = await betaAPI.getWearable({ bleMac: state.mac });
        if (active) setExisting(res.data || null);
      } catch (err) {
        // 404 = not registered (new unit). Any other failure: proceed as new;
        // a real duplicate would be caught by createWearable's 409.
        if (active) setExisting(null);
        if (err?.status && err.status !== 404) {
          console.error("Wearable lookup failed:", err);
        }
      }
    })();
    return () => {
      active = false;
    };
  }, [state.mac]);

  // Prompt countdown (reset per prompt id).
  const [remaining, setRemaining] = useState(null);
  const promptId = state.prompt?.id;
  useEffect(() => {
    if (!state.prompt) {
      setRemaining(null);
      return;
    }
    const total = Math.round((state.prompt.timeoutMs || 60000) / 1000);
    setRemaining(total);
    const iv = setInterval(
      () => setRemaining((r) => (r == null ? r : Math.max(0, r - 1))),
      1000
    );
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [promptId]);

  // Load batches when a new unit passes.
  useEffect(() => {
    if (phase !== PHASE.PASS || existing !== null) return;
    let active = true;
    (async () => {
      try {
        const res = await betaAPI.getBatches({ page: 1, pageSize: 100 });
        if (!active) return;
        const list = res.data?.results || [];
        setBatches(list);
        const first = list[0];
        if (first) {
          const s = String((first.wearable_count ?? 0) + 1);
          setBatchId(first.id);
          setSeq(s);
          setSerial(buildSerial(first, s));
        }
      } catch {
        // selector stays empty; surfaced on submit
      }
    })();
    return () => {
      active = false;
    };
  }, [phase, existing]);

  // Log a diagnostic attempt when an already-registered unit passes. Fired once
  // via a ref guard — `diagState` is NOT a dependency, because setting it inside
  // the effect would otherwise re-run the effect and cancel its own in-flight
  // request (leaving it stuck on "logging…").
  useEffect(() => {
    if (phase !== PHASE.PASS || !existing || diagStartedRef.current) return;
    diagStartedRef.current = true;
    setDiagState("logging");
    (async () => {
      try {
        const stage = existing.validation_stage || "baseline";
        await betaAPI.createWearableAttempt({
          bleMac: state.mac,
          body: {
            sub_stage: stage,
            step: "factory_test",
            result: "pass",
            data: factoryData(),
          },
        });
        setDiagState("logged");
      } catch (err) {
        setDiagState("error");
        setDiagError(
          formatApiError(err, t("deviceTools.factoryTest.diagLogFailed"))
        );
      }
    })();
    // eslint-disable-next-line
  }, [phase, existing]);

  // Compact snapshot of the run to store on the attempt — a passed/total summary
  // plus any failed test slugs, rather than the full per-test object (which
  // renders as an unreadable blob in the audit trail).
  const factoryData = () => {
    const entries = Object.entries(state.tests);
    const failed = entries
      .filter(([, test]) => test.status === "fail" || test.pass === false)
      .map(([slug]) => slug);
    const passedCount = entries.filter(
      ([, test]) => test.status === "pass" || test.pass === true
    ).length;
    return {
      mac: state.mac,
      fw: state.fw,
      mask: state.ended?.mask ?? null,
      result: `${passedCount}/${FACTORY_TESTS.length} passed`,
      ...(failed.length ? { failed: failed.join(", ") } : {}),
    };
  };

  // Answer an open prompt. The console needs a DTR edge to read the byte, and a
  // single write can be missed, so we resend + kick until the prompt closes
  // (the device acks / emits a result). Crucially we re-check that the prompt is
  // STILL open right before each kick — once the ack lands we stop immediately,
  // so no kick fires after the run resolves (which would restart the test).
  const answer = (val) => {
    if (!connected || !state.prompt || endedRef.current) return;
    const targetId = state.prompt.id;
    (async () => {
      for (let i = 0; i < 4; i++) {
        if (promptIdRef.current !== targetId || endedRef.current) break;
        await sendRaw(answerBytes(val)).catch(() => {});
        await sleep(140);
        if (promptIdRef.current !== targetId || endedRef.current) break;
        await kick?.().catch?.(() => {});
        await sleep(1200);
      }
    })();
  };

  const exit = () => {
    // Leave the device in a clean state if a run was mid-flight.
    send("idle").catch(() => {});
    onExit();
  };

  const retry = () => setRunId((n) => n + 1);

  const onBatchChange = (id) => {
    const b = batches.find((x) => x.id === id);
    const s = String((b?.wearable_count ?? 0) + 1);
    setBatchId(id);
    setSeq(s);
    if (!serialEdited) setSerial(buildSerial(b, s));
  };

  const onSeqChange = (v) => {
    setSeq(v);
    if (!serialEdited) {
      const b = batches.find((x) => x.id === batchId);
      setSerial(buildSerial(b, v));
    }
  };

  const canRegister =
    batchId && serial.trim().length > 0 && state.mac && !saving;

  const handleRegister = async () => {
    if (!canRegister) return;
    setSaving(true);
    setRegError(null);
    try {
      await betaAPI.createWearable({
        body: {
          ble_mac: state.mac,
          serial: serial.trim(),
          batch_id: batchId,
          unit_type: unitType,
        },
      });
      // Registration IS the Baseline factory_test — log the real result. If the
      // backend auto-completes baseline on register, this is harmlessly rejected.
      try {
        await betaAPI.createWearableAttempt({
          bleMac: state.mac,
          body: {
            sub_stage: "baseline",
            step: "factory_test",
            result: "pass",
            data: factoryData(),
          },
        });
      } catch {
        // out-of-order attempt is harmless — the unit is registered either way
      }
      showToast.success(t("deviceTools.factoryTest.registered"));
      onExit();
    } catch (err) {
      setRegError(
        formatApiError(err, t("deviceTools.factoryTest.registerFailed"))
      );
    } finally {
      setSaving(false);
    }
  };

  // Checklist rows: catalog order, overlaid with live status. An overall pass
  // greens any row we didn't see an explicit result for (mask 0 = all passed).
  const rows = FACTORY_TESTS.map((test) => {
    const live = state.tests[test.slug] || {};
    let status = live.status || "pending";
    if (phase === PHASE.PASS) status = "pass";
    return { ...test, ...live, status };
  });
  const passedCount = rows.filter((r) => r.status === "pass").length;
  const failedRows = rows.filter((r) => r.status === "fail");

  return (
    <div className="mx-auto flex h-full max-w-xl flex-col">
      {/* Header */}
      <div className="mb-4 flex items-center gap-3">
        <button
          onClick={exit}
          className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-[#E5E7EB] bg-white text-[#6B7280] transition duration-150 hover:bg-[#F9FAFB] hover:text-[#374151]"
          title={t("deviceTools.factoryTest.backToExport")}
        >
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#111827]">
            {t("deviceTools.factoryTest.title")}
          </p>
          <p className="truncate text-[11px] text-[#9CA3AF]">
            {state.mac
              ? formatMac(state.mac)
              : t("deviceTools.shared.readingDevice")}
            {state.fw ? ` · ${state.fw}` : ""}
          </p>
        </div>
        <span className="flex-shrink-0 rounded-full bg-[#F3F4F6] px-2.5 py-1 text-[11px] font-semibold text-[#6B7280]">
          {t("deviceTools.factoryTest.passedCount", {
            passed: passedCount,
            total: rows.length,
          })}
        </span>
      </div>

      {/* Not started yet */}
      {!state.started && (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-center">
          <Loader2 size={26} className="animate-spin text-[#E07A3A]" />
          <p className="text-sm text-[#6B7280]">
            {commandsLocked
              ? t("deviceTools.shared.waitingForConsole")
              : t("deviceTools.factoryTest.starting")}
          </p>
        </div>
      )}

      {state.started && (
        <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto">
          {/* Active interactive prompt */}
          {phase === PHASE.TESTING && state.prompt && (
            <>
              {state.prompt.expect === "press" ? (
                <ButtonPrompt
                  prompt={state.prompt}
                  remaining={remaining}
                  onFail={() => answer(false)}
                />
              ) : (
                <LedPrompt
                  prompt={state.prompt}
                  ledSteps={state.ledSteps}
                  onAnswer={answer}
                />
              )}
            </>
          )}

          {/* Pass banner */}
          {phase === PHASE.PASS && (
            <div className="flex items-center gap-2.5 rounded-xl border border-[#BBF7D0] bg-[#F0FDF4] px-4 py-3">
              <CheckCircle2
                size={18}
                className="flex-shrink-0 text-[#16A34A]"
              />
              <p className="text-sm font-medium text-[#15803D]">
                {t("deviceTools.factoryTest.allPassed")}
              </p>
            </div>
          )}

          {/* Fail banner */}
          {phase === PHASE.FAIL && (
            <div className="rounded-xl border border-[#FECACA] bg-[#FEF2F2] px-4 py-3">
              <div className="flex items-center gap-2.5">
                <XCircle size={18} className="flex-shrink-0 text-[#DC2626]" />
                <p className="text-sm font-medium text-[#B91C1C]">
                  {failedRows.length > 0
                    ? t("deviceTools.factoryTest.failedWithTests", {
                        tests: failedRows.map((r) => r.label).join(", "),
                      })
                    : t("deviceTools.factoryTest.failed")}
                </p>
              </div>
              <p className="mt-1 pl-7 text-xs text-[#DC2626]">
                {t("deviceTools.factoryTest.registrationBlocked")}
              </p>
            </div>
          )}

          {/* Checklist */}
          <div className="divide-y divide-[#F3F4F6] rounded-xl border border-[#F3F4F6] bg-white">
            {rows.map((row) => (
              <TestRow key={row.slug} test={row} />
            ))}
          </div>

          {/* Fail actions */}
          {phase === PHASE.FAIL && (
            <button
              onClick={retry}
              disabled={!connected}
              className="flex items-center justify-center gap-2 rounded-[10px] border border-[#E5E7EB] bg-white px-4 py-2.5 text-sm font-semibold text-[#374151] transition duration-150 hover:bg-[#F9FAFB] disabled:opacity-50"
            >
              <RotateCcw size={15} />
              {t("deviceTools.factoryTest.retryTest")}
            </button>
          )}

          {/* Existing unit — diagnostic only */}
          {phase === PHASE.PASS && existing && (
            <div className="rounded-xl border border-[#F3F4F6] bg-[#F9FAFB] px-4 py-3.5">
              <p className="text-sm font-medium text-[#111827]">
                {t("deviceTools.factoryTest.alreadyRegistered")}
              </p>
              <p className="mt-0.5 text-xs text-[#6B7280]">
                {existing.serial ? `${existing.serial} · ` : ""}
                {diagState === "logging" &&
                  t("deviceTools.factoryTest.diagLogging")}
                {diagState === "logged" &&
                  t("deviceTools.factoryTest.diagLogged")}
                {diagState === "error" &&
                  (diagError || t("deviceTools.factoryTest.diagError"))}
                {diagState === "idle" && t("deviceTools.factoryTest.diagIdle")}
              </p>
              <button
                onClick={onExit}
                className="mt-3 flex w-full items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150"
                style={{
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                }}
              >
                {t("common.actions.done")}
              </button>
            </div>
          )}

          {/* New unit — register */}
          {phase === PHASE.PASS && existing === null && (
            <div className="rounded-xl border border-[#F3F4F6] bg-white p-4">
              <p className="mb-3 text-sm font-semibold text-[#111827]">
                {t("deviceTools.factoryTest.registerTitle")}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <SelectField
                  className="col-span-2"
                  label={t("deviceTools.factoryTest.batch")}
                  value={batchId}
                  onChange={onBatchChange}
                  options={batches.map((b) => ({ id: b.id, label: b.code }))}
                  placeholder={t("deviceTools.factoryTest.selectBatch")}
                  required
                />
                <TextField
                  label={t("deviceTools.factoryTest.sequence")}
                  value={seq}
                  onChange={onSeqChange}
                  placeholder="1"
                  type="number"
                  required
                />
                <SelectField
                  label={t("deviceTools.factoryTest.unitType")}
                  value={unitType}
                  onChange={setUnitType}
                  options={UNIT_TYPE_OPTIONS}
                  placeholder=""
                />
                <TextField
                  className="col-span-2"
                  label={t("deviceTools.factoryTest.serial")}
                  value={serial}
                  onChange={(v) => {
                    setSerialEdited(true);
                    setSerial(v);
                  }}
                  placeholder="SIT-W1-2624-00001"
                  maxLength={24}
                  required
                  hint={
                    !serialEdited
                      ? t("deviceTools.factoryTest.serialHint")
                      : undefined
                  }
                />
              </div>

              {regError && (
                <p className="mt-3 whitespace-pre-line text-xs text-[#DC2626]">
                  {regError}
                </p>
              )}

              <button
                onClick={handleRegister}
                disabled={!canRegister}
                className="mt-4 flex w-full items-center justify-center gap-1.5 rounded-[10px] px-4 py-2.5 text-sm font-semibold text-white transition duration-150 disabled:opacity-50"
                style={{
                  background:
                    "linear-gradient(135deg, #E8854A 0%, #D66B2E 100%)",
                }}
              >
                {saving ? (
                  <Loader2 size={15} className="animate-spin" />
                ) : (
                  <ArrowRight size={15} />
                )}
                {t("deviceTools.factoryTest.registerUnit")}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default FactoryTestFlow;
